import { MenuProps } from '@mui/material'
import { green, red, yellow } from '@mui/material/colors'
import { useEffect, useState } from 'react'
import screenfull from 'screenfull'

interface ConnectionMarker {
  /** The band, for control flow (which notice to show), kept separate from the display copy. */
  band: 'poor' | 'ok' | 'good'
  /** The word for the connection, spoken by a tile's tooltip and the self indicator's label. */
  text: string
  /** The colour that carries the same reading, so neither is the only way to read it. */
  color: string
}

/**
 * A connection quality as something a provider can see: the band, the word for it, and the colour.
 *
 * `quality` is the `Math.min` of a participant's uplink and downlink link levels (0-3), the value
 * `SessionCallV2` already derives for the tiles. Shared by each tile's badge and the provider's own
 * connection indicator, so the two never disagree about what a "poor" connection looks like. The
 * thresholds match the shared package's `toNetworkStatus` banding (0-1 bad, 2 ok, 3 good). `band`
 * exists so callers branch on it rather than on the display copy, which is then free to change.
 *
 * `undefined` means the SDK has reported nothing yet, which every caller reads as "show no
 * indicator at all".
 */
export const connectionMarker = (quality: number | undefined): ConnectionMarker | undefined => {
  if (typeof quality === 'undefined') return undefined
  if (quality < 2) return { band: 'poor', text: 'Poor connection', color: red[500] }
  if (quality < 3) return { band: 'ok', text: 'Ok connection', color: yellow[500] }
  return { band: 'good', text: 'Good connection', color: green[500] }
}

type ConnectionEffectiveType = 'slow-2g' | '2g' | '3g' | '4g'
interface NetworkInformation extends EventTarget {
  effectiveType: ConnectionEffectiveType
  downlink: number
  onchange: (e: Event) => void
  rtt: number
  saveData: boolean
}

// Declaration merge adding effectiveType to the Connection interface
declare global {
  interface Navigator {
    connection: NetworkInformation
  }
}

export const useConnectionStrength = () => {
  // Track network strength if supported by the browser
  const supportsNetworkConnection = !!navigator.connection && !!navigator.connection.effectiveType
  const [network, setNetwork] = useState(
    supportsNetworkConnection ? navigator.connection.effectiveType : null,
  )
  useEffect(() => {
    if (!supportsNetworkConnection) return
    // Guarded above, and the `change` event targets this same object.
    const connection = navigator.connection
    const handleChange = () => setNetwork(connection.effectiveType)
    connection.addEventListener('change', handleChange)
    return () => {
      connection.removeEventListener('change', handleChange)
    }
  }, [supportsNetworkConnection])

  // Translate network strength to a number
  const networkQuality = {
    'slow-2g': 1,
    '2g': 2,
    '3g': 3,
    '4g': 4,
  }[network ?? '4g']
  const isPoorConnection = networkQuality < 3
  return { network, isPoorConnection, networkQuality }
}

/**
 * Where the device menus portal to, resolved each time a menu opens.
 *
 * The fullscreen button (`SessionHeader`) fullscreens the visit container via the browser Fullscreen
 * API, which paints only that element's subtree — so a menu portaled to `document.body` (MUI's
 * default) is invisible while the visit is fullscreen. Portaling into the fullscreen element fixes
 * that. Everywhere else `document.body` is used, because portaling into the modal's own (offset,
 * positioned) panel makes the Popover compute its position against that box and land in the wrong
 * place; the fullscreen top layer fills the screen and does not have that problem. `screenfull.element`
 * is whichever element is fullscreen (or undefined), normalized across browsers as `SessionHeader`
 * already relies on.
 */
const menuContainer = (): Element => (screenfull.isEnabled && screenfull.element) || document.body

export const useHardwareMenusEls = () => {
  const [audioAnchorEl, setAudioAnchorEl] = useState<null | HTMLElement>(null)
  const [videoAnchorEl, setVideoAnchorEl] = useState<null | HTMLElement>(null)
  const [speakersAnchorEl, setSpeakersAnchorEl] = useState<null | HTMLElement>(null)
  const handleCloseAudio = () => setAudioAnchorEl(null)
  const handleCloseVideo = () => setVideoAnchorEl(null)
  const handleCloseSpeakers = () => setSpeakersAnchorEl(null)
  const handleCloseAll = () => {
    handleCloseAudio()
    handleCloseVideo()
    handleCloseSpeakers()
  }
  const handleMenuClick =
    (setter: typeof setAudioAnchorEl) => (event: React.MouseEvent<HTMLButtonElement>) => {
      setter(event.currentTarget)
    }
  const handleOpenAudio = handleMenuClick(setAudioAnchorEl)
  const handleOpenVideo = handleMenuClick(setVideoAnchorEl)
  const handleOpenSpeakers = handleMenuClick(setSpeakersAnchorEl)

  const audioMenuProps: MenuProps = {
    open: Boolean(audioAnchorEl),
    anchorEl: audioAnchorEl,
    onClose: handleCloseAudio,
    container: menuContainer,
  }
  const videoMenuProps: MenuProps = {
    open: Boolean(videoAnchorEl),
    anchorEl: videoAnchorEl,
    onClose: handleCloseVideo,
    container: menuContainer,
  }
  const speakersMenuProps: MenuProps = {
    open: Boolean(speakersAnchorEl),
    anchorEl: speakersAnchorEl,
    onClose: handleCloseSpeakers,
    container: menuContainer,
  }

  return {
    handleOpenAudio,
    handleOpenVideo,
    handleOpenSpeakers,
    handleCloseAll,
    audioMenuProps,
    videoMenuProps,
    speakersMenuProps,
  }
}
